import { Chat, FolderOpen, InsertDriveFile, PictureAsPdf } from '@mui/icons-material'
import { inject } from 'mobx-react'
import React from 'react'
import CommunicationsDialog from '../../Communications/CommunicationsDialog'
import EditCommunicationDialog from '../../Communications/EditCommunicationDialog'
import { ICommunication, IEpisodeOfCare, IPatient } from '../../Definitions'
import PatientSMSDialog from '../../patients/PatientSMSDialog'
import { IStores } from '../../Stores'
import ActionBar, { IActionBarItem } from './ActionBar'
import * as PatientService from '../../services/PatientService'
import { SharedDrivePathRequest } from '../../viewModels/SharedDrivePathRequest'

interface IPatientActionBarProps {
  episodeOfCare?: IEpisodeOfCare
  closePatientSMSDialog?: () => void
  generatePatientDemographicsReport?: () => void
  isDemographicsVisible?: boolean
  isCommuicationEditModalOpen?: boolean
  isCommunicationOpen?: boolean
  isPatientSMSDialogOpen?: boolean
  loadFullCommunications?: () => Promise<void>
  openCommunicationDialog?: () => void
  openDialogWithCommunication?: (communication: ICommunication) => void
  openPatientSMSDialog?: () => void
  patient?: IPatient
}

class PatientActionBar extends React.Component<IPatientActionBarProps> {
  public openCommunicationsDialog = async () => {
    await this.props.loadFullCommunications!().then(() => {
      this.props.openCommunicationDialog!()
    })
  }

  public openDialog = (communication: ICommunication) => () => {
    if (this.props.patient && !this.props.patient.isNew) {
      this.props.openDialogWithCommunication!(communication)
    }
  }

  public render() {
    const { isDemographicsVisible = false, isCommunicationOpen } = this.props
    const patientSMSDialog = this.props.isPatientSMSDialogOpen && <PatientSMSDialog />
    const fullCommunicationDialog = isCommunicationOpen && (
      <CommunicationsDialog open={isCommunicationOpen} />
    )
    const communicationDialog = this.props.isCommuicationEditModalOpen && (
      <EditCommunicationDialog />
    )
    let actionBarItems: IActionBarItem[] = [
      {
        icon: <Chat />,
        label: 'Comm Log',
        onClick: async () => {
          await this.props.loadFullCommunications!().then(() => {
            this.props.openCommunicationDialog!()
          })
        },
      },
      {
        icon: <InsertDriveFile />,
        label: 'Patient Docs',
        onClick: () => {
          window.open(`/patients/patient/${this.props.patient?.id}/documents`)
        },
      },
      {
        disabled: !this.props.patient || this.props.patient.isNew,
        icon: <FolderOpen />,
        label: 'Shared Files',
        onClick: async () => {
          if (!this.props.patient) return
          try {
            let path = await this.getPatientFilePath()
            if (path) {
              path = path.replace(/^\/+|\/+$/g, '')
              window.open(`/files?path=${encodeURIComponent(path)}`)
            }
          } catch (error) {
            console.error('Failed to get patient file path:', error)
            alert('Unable to determine shared files path for this patient.')
          }
        },
      },
    ]

    if (isDemographicsVisible) {
      actionBarItems = actionBarItems.concat([
        {
          icon: <PictureAsPdf />,
          label: 'Demographics',
          onClick: () => {
            this.props.generatePatientDemographicsReport!()
          },
        },
      ])
    }

    return (
      <>
        <ActionBar items={actionBarItems} patient={this.props.patient} />
        {patientSMSDialog}
        {fullCommunicationDialog}
        {communicationDialog}
      </>
    )
  }

  private getPatientFilePath = async (): Promise<string> => {
    const { patient } = this.props
    if (!patient) return ''

    const request = new SharedDrivePathRequest()
    request.patientFirst = patient.firstName || ''
    request.patientLast = patient.lastName || ''
    request.preCertNumber = patient.preCertNumber

    try {
      const path = await PatientService.GetSharedDrivePath(patient.id, request)
      return path
    } catch (error) {
      console.error('Backend API failed, constructing path client-side:', error)
      const lastName = (patient.lastName || '').replace(/\s+/g, '_')
      const firstName = (patient.firstName || '').replace(/\s+/g, '_')
      const preCertNumber = patient.preCertNumber || patient.id || ''
      return `${lastName}_${firstName}_${preCertNumber}`
    }
  }
}

const InjectedPatientActionBar = inject<
  IStores,
  IPatientActionBarProps,
  Partial<IPatientActionBarProps>,
  any
>((stores: IStores) => ({
  episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
  generatePatientDemographicsReport: stores.patientEdit.generatePatientDemographicsReport,
  isCommuicationEditModalOpen: stores.communications.isEditModalOpen,
  isCommunicationOpen: stores.communications.isModalOpen,
  isPatientSMSDialogOpen: stores.patients.isPatientSMSDialogOpen,
  loadFullCommunications: stores.communications.getColumnSettingsAndFullCommunications,
  openCommunicationDialog: stores.communications.openDialog,
  openDialogWithCommunication: stores.communications.openDialogWithCommunication,
  openPatientSMSDialog: stores.patients.openPatientSMSDialog,
  patient: stores.patients.selectedPatient,
}))(PatientActionBar)

export default InjectedPatientActionBar
